import type { CanvasCard, SizePreset } from "store/types";

import { buildChartConfig, buildKpiConfig, buildSummaryConfig, buildTableConfig } from "./cardDefaults";

function uid() {
  return `card-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export interface Layout {
  id: string;
  label: string;
  desc: string;
  rows: number[][];
  build: () => CanvasCard[];
}

function c(
  type: CanvasCard["type"],
  widthPercent: number,
  config: CanvasCard["config"],
  page: number,
  rowIndex: number,
  orderInRow: number,
  preset: SizePreset | null = null
): CanvasCard {
  return { id: uid(), type, widthPercent, lastPreset: preset, rowIndex, orderInRow, config, page };
}

export const LAYOUTS: Layout[] = [
  {
    id: "executive",
    label: "Executive Summary",
    desc: "KPI row, chart, and commentary",
    rows: [[33.33, 33.33, 33.33], [50, 50], [100]],
    build: () => [
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 0, "S"),
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 1, "S"),
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 2, "S"),
      c("chart", 50, buildChartConfig(), 0, 1, 0, "M"),
      c("text", 50, { type: "text", content: "" }, 0, 1, 1, "M"),
      c("table", 100, buildTableConfig(), 0, 2, 0, "F")
    ]
  },
  {
    id: "header-right",
    label: "Header + Details Right",
    desc: "Summary left, KPIs right, table below",
    rows: [[66.67, 33.33], [66.67, 33.33], [100]],
    build: () => [
      c("summary", 66.67, buildSummaryConfig(), 0, 0, 0),
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 1, "S"),
      c("chart", 66.67, buildChartConfig(), 0, 1, 0),
      c("kpi", 33.33, buildKpiConfig(), 0, 1, 1, "S"),
      c("table", 100, buildTableConfig(), 0, 2, 0, "F")
    ]
  },
  {
    id: "header-left",
    label: "Header + Details Left",
    desc: "KPIs left, charts right, table below",
    rows: [[33.33, 66.67], [33.33, 66.67], [100]],
    build: () => [
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 0, "S"),
      c("chart", 66.67, buildChartConfig(), 0, 0, 1),
      c("kpi", 33.33, buildKpiConfig(), 0, 1, 0, "S"),
      c("chart", 66.67, buildChartConfig(), 0, 1, 1),
      c("table", 100, buildTableConfig(), 0, 2, 0, "F")
    ]
  },
  {
    id: "data-focus",
    label: "Data Focus",
    desc: "Charts with a full deal table",
    rows: [[33.33, 33.33, 33.33], [66.67, 33.33], [100]],
    build: () => [
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 0, "S"),
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 1, "S"),
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 2, "S"),
      c("chart", 66.67, buildChartConfig(), 0, 1, 0),
      c("chart", 33.33, buildChartConfig(), 0, 1, 1, "S"),
      c("table", 100, buildTableConfig(), 0, 2, 0, "F")
    ]
  },
  {
    id: "narrative",
    label: "Narrative Report",
    desc: "Commentary with supporting data",
    rows: [[100], [66.67, 33.33], [100]],
    build: () => [
      c("summary", 100, buildSummaryConfig(), 0, 0, 0, "F"),
      c("text", 66.67, { type: "text", content: "" }, 0, 1, 0),
      c("chart", 33.33, buildChartConfig(), 0, 1, 1, "S"),
      c("table", 100, buildTableConfig(), 0, 2, 0, "F")
    ]
  },
  {
    id: "capital-account",
    label: "Capital Account Statement",
    desc: "Per-investor. Commitment, called, distributed, NAV, unfunded, MOIC.",
    rows: [[33.33, 33.33, 33.33], [100], [66.67, 33.33]],
    build: () => [
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 0, "S"),
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 1, "S"),
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 2, "S"),
      c("table", 100, buildTableConfig(), 0, 1, 0, "F"),
      c("unfunded", 66.67, { type: "unfunded" }, 0, 2, 0),
      c("kpi", 33.33, buildKpiConfig(), 0, 2, 1, "S")
    ]
  },
  {
    id: "portfolio-briefing",
    label: "Portfolio Briefing",
    desc: "Principal-facing. What changed since last visit, top movers, recent capital activity.",
    rows: [[33.33, 33.33, 33.33], [66.67, 33.33], [100]],
    build: () => [
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 0, "S"),
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 1, "S"),
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 2, "S"),
      c("text", 66.67, { type: "text", content: "" }, 0, 1, 0),
      c("performance", 33.33, { type: "performance", sortBy: "irr", topN: 3 }, 0, 1, 1, "S"),
      c("cashflow", 100, { type: "cashflow" }, 0, 2, 0, "F")
    ]
  },
  {
    id: "vintage-attribution",
    label: "Vintage Year Attribution",
    desc: "IRR/MOIC grouped by vintage cohort. Investment committee use.",
    rows: [[33.33, 33.33, 33.33], [66.67, 33.33], [100]],
    build: () => [
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 0, "S"),
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 1, "S"),
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 2, "S"),
      c("exposure", 66.67, { type: "exposure", dimension: "Vintage", chartType: "bar", measure: "exposure" }, 0, 1, 0),
      c("performance", 33.33, { type: "performance", sortBy: "moic", topN: 5 }, 0, 1, 1, "S"),
      c("table", 100, buildTableConfig(), 0, 2, 0, "F")
    ]
  },
  {
    id: "gp-manager-summary",
    label: "GP / Manager Summary",
    desc: "All deals with a given manager aggregated.",
    rows: [[100], [66.67, 33.33], [100]],
    build: () => [
      c("summary", 100, buildSummaryConfig(), 0, 0, 0, "F"),
      c("chart", 66.67, buildChartConfig(), 0, 1, 0),
      c("benchmark", 33.33, { type: "benchmark", benchmarkIds: ["^SPX"] }, 0, 1, 1, "S"),
      c("table", 100, buildTableConfig(), 0, 2, 0, "F")
    ]
  },
  {
    id: "exposure-breakdown",
    label: "Exposure Breakdown",
    desc: "Concentration by asset class, geography, strategy, vintage.",
    rows: [[66.67, 33.33], [66.67, 33.33], [33.33]],
    build: () => [
      c(
        "exposure",
        66.67,
        { type: "exposure", dimension: "assetClass", chartType: "donut", measure: "exposure" },
        0,
        0,
        0
      ),
      c(
        "exposure",
        33.33,
        { type: "exposure", dimension: "Geography", chartType: "bar", measure: "exposure" },
        0,
        0,
        1,
        "S"
      ),
      c(
        "exposure",
        66.67,
        { type: "exposure", dimension: "Strategy", chartType: "donut", measure: "exposure" },
        0,
        1,
        0
      ),
      c(
        "exposure",
        33.33,
        { type: "exposure", dimension: "Vintage", chartType: "bar", measure: "exposure" },
        0,
        1,
        1,
        "S"
      ),
      c("dataFreshness", 33.33, { type: "dataFreshness" }, 0, 2, 0, "S")
    ]
  },
  {
    id: "quarterly-letter",
    label: "Quarterly Letter",
    desc: "Narrative commentary with supporting KPIs.",
    rows: [[100], [66.67, 33.33], [100], [100]],
    build: () => [
      c(
        "macroBrief",
        100,
        { type: "macroBrief", indicators: [] },
        0,
        0,
        0,
        "F"
      ),
      c("text", 66.67, { type: "text", content: "" }, 0, 1, 0),
      c("chart", 33.33, buildChartConfig(), 0, 1, 1, "S"),
      c("kpi", 100, buildKpiConfig(), 0, 2, 0, "F"),
      c("cashflow", 100, { type: "cashflow" }, 0, 3, 0, "F")
    ]
  },
  {
    id: "one-pager-deal",
    label: "One-Pager Deal Summary",
    desc: "Single deal, all key characteristics and measures.",
    rows: [
      [33.33, 66.67],
      [66.67, 33.33]
    ],
    build: () => [
      c("kpi", 33.33, buildKpiConfig(), 0, 0, 0, "S"),
      c("summary", 66.67, buildSummaryConfig(), 0, 0, 1),
      c("benchmark", 66.67, { type: "benchmark", benchmarkIds: ["^SPX"] }, 0, 1, 0),
      c("dataFreshness", 33.33, { type: "dataFreshness" }, 0, 1, 1, "S")
    ]
  },
  {
    id: "annual-report-5p",
    label: "Annual Portfolio Report",
    desc: "5-page pack: executive summary, performance, exposure, capital account, market context.",
    rows: [[33.33, 33.33, 33.33], [66.67, 33.33], [100]],
    build: () => {
      const p0: CanvasCard[] = [
        c("kpi", 33.33, buildKpiConfig(), 0, 0, 0, "S"),
        c("kpi", 33.33, buildKpiConfig(), 0, 0, 1, "S"),
        c("kpi", 33.33, buildKpiConfig(), 0, 0, 2, "S"),
        c("summary", 66.67, buildSummaryConfig(), 0, 1, 0),
        c("kpi", 33.33, buildKpiConfig(), 0, 1, 1, "S"),
        c("chart", 66.67, buildChartConfig(), 0, 2, 0),
        c("chart", 33.33, buildChartConfig(), 0, 2, 1, "S"),
        c("table", 100, buildTableConfig(), 0, 3, 0, "F")
      ];
      const p1: CanvasCard[] = [
        c("performance", 66.67, { type: "performance", sortBy: "irr", topN: 5 }, 1, 0, 0),
        c("benchmark", 33.33, { type: "benchmark", benchmarkIds: ["^SPX"] }, 1, 0, 1, "S"),
        c("cashflow", 100, { type: "cashflow" }, 1, 1, 0, "F"),
        c("table", 100, buildTableConfig(), 1, 2, 0, "F")
      ];
      const p2: CanvasCard[] = [
        c(
          "exposure",
          50,
          { type: "exposure", dimension: "assetClass", chartType: "donut", measure: "exposure" },
          2,
          0,
          0,
          "M"
        ),
        c(
          "exposure",
          50,
          { type: "exposure", dimension: "Geography", chartType: "bar", measure: "exposure" },
          2,
          0,
          1,
          "M"
        ),
        c(
          "exposure",
          50,
          { type: "exposure", dimension: "Strategy", chartType: "donut", measure: "exposure" },
          2,
          1,
          0,
          "M"
        ),
        c(
          "exposure",
          50,
          { type: "exposure", dimension: "Vintage", chartType: "bar", measure: "exposure" },
          2,
          1,
          1,
          "M"
        ),
        c("unfunded", 66.67, { type: "unfunded" }, 2, 2, 0),
        c("dataFreshness", 33.33, { type: "dataFreshness" }, 2, 2, 1, "S")
      ];
      const p3: CanvasCard[] = [
        c("kpi", 33.33, buildKpiConfig(), 3, 0, 0, "S"),
        c("kpi", 33.33, buildKpiConfig(), 3, 0, 1, "S"),
        c("kpi", 33.33, buildKpiConfig(), 3, 0, 2, "S"),
        c("table", 100, buildTableConfig(), 3, 1, 0, "F"),
        c("unfunded", 66.67, { type: "unfunded" }, 3, 2, 0),
        c("kpi", 33.33, buildKpiConfig(), 3, 2, 1, "S")
      ];
      const p4: CanvasCard[] = [
        c(
          "macroBrief",
          100,
          {
            type: "macroBrief",
            indicators: []
          },
          4,
          0,
          0,
          "F"
        ),
        c("text", 66.67, { type: "text", content: "" }, 4, 1, 0),
        c("kpi", 33.33, buildKpiConfig(), 4, 1, 1, "S")
      ];
      return [...p0, ...p1, ...p2, ...p3, ...p4];
    }
  },
  {
    id: "investment-committee",
    label: "Investment Committee Pack",
    desc: "Fixed sequence: executive summary, deal one-pagers, exposure breakdown, macro brief.",
    rows: [[33.33, 33.33, 33.33], [66.67, 33.33], [100]],
    build: () => {
      const p0: CanvasCard[] = [
        c("kpi", 33.33, buildKpiConfig(), 0, 0, 0, "S"),
        c("kpi", 33.33, buildKpiConfig(), 0, 0, 1, "S"),
        c("kpi", 33.33, buildKpiConfig(), 0, 0, 2, "S"),
        c("performance", 66.67, { type: "performance", sortBy: "irr", topN: 5 }, 0, 1, 0),
        c("benchmark", 33.33, { type: "benchmark", benchmarkIds: ["^SPX"] }, 0, 1, 1, "S"),
        c("table", 100, buildTableConfig(), 0, 2, 0, "F")
      ];
      const p1: CanvasCard[] = [
        c(
          "exposure",
          66.67,
          { type: "exposure", dimension: "assetClass", chartType: "donut", measure: "exposure" },
          1,
          0,
          0
        ),
        c(
          "exposure",
          33.33,
          { type: "exposure", dimension: "Geography", chartType: "bar", measure: "exposure" },
          1,
          0,
          1,
          "S"
        ),
        c("cashflow", 66.67, { type: "cashflow" }, 1, 1, 0),
        c("unfunded", 33.33, { type: "unfunded" }, 1, 1, 1, "S")
      ];
      const p2: CanvasCard[] = [
        c(
          "macroBrief",
          100,
          {
            type: "macroBrief",
            indicators: []
          },
          2,
          0,
          0,
          "F"
        ),
        c("dataFreshness", 33.33, { type: "dataFreshness" }, 2, 1, 0, "S")
      ];
      return [...p0, ...p1, ...p2];
    }
  }
];
